/**
 * Static prototype admin service — backed by the in-memory mock store.
 * Public method signatures match the original API client.
 */

import { isConsolidated } from '../utils/classificationUtils';
import {
  AdminApplicationStatus,
  AdminUserFilters,
  AdminUsersResponse,
  AlertFilters,
  AlertPriority,
  AlertStatus,
  AlertType,
  ApplicationAlert,
  ApplicationEvaluationsResponseDto,
  ApplicationWithAdminData,
  EvalBoardAssignment,
  EvalBoardRow,
  EvalByEvaluatorAssignment,
  EvalByEvaluatorRow,
  EvaluationListItemDto,
  PaginatedAlertsResponse,
  PaginatedApplicationResponse,
  ProgressLabel,
  ResultLabel,
  SingleEvaluationResponseDto,
} from '../types/admin';
import { User } from '../types/auth';
import {
  AssignEvaluatorRequest,
  Evaluator,
  EvaluatorAssignment,
  InviteEvaluatorRequest,
  InviteEvaluatorResponse,
} from '../types/evaluator';
import { db, delay, newId, persist } from '../mocks/store';
import {
  buildEvaluationSummary,
  findApp,
  findUser,
  toAdminApplication,
  toEvaluator,
  toEvaluatorAssignment,
  weightedScore,
} from '../mocks/mappers';
import { CRITERIA_KEYS } from '../mocks/seed';
import { ALERT_CATALOG, ALERT_CONFIG } from '../components/admin/alertCatalog';
import { triggerDownload } from '../utils/download';

// Helper: weighted score on 0–5 scale from criteriaEvaluations map
const calcFinalScore = (
  criteriaEvals: Record<string, { score: number; maxScore: number }>
): number => {
  const entries = Object.values(criteriaEvals);
  if (!entries.length) return 0;
  const total = entries.reduce((s, c) => s + c.score, 0);
  const max = entries.reduce((s, c) => s + c.maxScore, 0);
  return max === 0 ? 0 : (total / max) * 5;
};

// Custom error class for admin service operations
class AdminServiceError extends Error {
  constructor(message: string, public status?: number, public code?: string) {
    super(message);
    this.name = 'AdminServiceError';
  }
}

/**
 * La asignación existe pero nadie avisó al evaluador.
 *
 * `status: 'pending'` por sí solo no lo distingue: significa "sin respuesta", y
 * una asignación recién creada sin enviar correo también está sin respuesta.
 * Esta es la única definición de "sin invitar" en el código — todo lo que
 * dependa de ella (cobertura, alertas, envío masivo) la importa desde aquí.
 */
export const isNotInvited = (a: {
  status: string;
  invitationSentAt?: string | null;
}): boolean => a.status === 'pending' && !a.invitationSentAt;

const paginate = (total: number, page: number, limit: number) => ({
  page,
  limit,
  total,
  totalPages: Math.max(1, Math.ceil(total / limit)),
});

const pushHistory = (
  app: ReturnType<typeof findApp> & {},
  status: AdminApplicationStatus,
  notes?: string
) => {
  const ts = new Date().toISOString();
  app.statusHistory = [
    ...app.statusHistory,
    {
      status,
      changedBy: '33333333-3333-4333-8333-000000000003',
      changedByName: 'Admin DINV',
      changedAt: ts,
      notes,
    },
  ];
  app.status = status;
  app.updatedAt = ts;
};

class AdminService {
  // ── Template scoping helpers ───────────────────────────────────────────────
  private appsForTemplate(formTemplateId?: string | null) {
    if (!formTemplateId) return db.applications;
    return db.applications.filter((a) => a.formTemplateId === formTemplateId);
  }
  private appIdsForTemplate(formTemplateId?: string | null): Set<string> {
    return new Set(this.appsForTemplate(formTemplateId).map((a) => a.id));
  }

  // Returns the list of contests (formTemplates) for the AdminHome page.
  async getContests(): Promise<
    Array<{
      id: string;
      name: string;
      description: string;
      year: number | null;
      isActive: boolean;
      isPublished: boolean;
      openDate: string | null;
      deadline: string;
      applicationCount: number;
    }>
  > {
    const list = db.formTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      year: t.year ?? null,
      isActive: t.isActive,
      isPublished: t.isPublished ?? true,
      openDate: t.openDate ?? null,
      deadline: t.deadline,
      applicationCount: db.applications.filter((a) => a.formTemplateId === t.id).length,
    }));
    list.sort(
      (a, b) =>
        new Date(b.openDate ?? b.deadline).getTime() -
        new Date(a.openDate ?? a.deadline).getTime()
    );
    return delay(list);
  }

  async getContest(id: string): Promise<{
    id: string; name: string; description: string;
    year: number | null; isActive: boolean; isPublished: boolean;
    openDate: string | null; deadline: string; applicationCount: number;
  } | null> {
    const t = db.formTemplates.find((t) => t.id === id);
    if (!t) return delay(null);
    return delay({
      id: t.id, name: t.name, description: t.description,
      year: t.year ?? null, isActive: t.isActive, isPublished: t.isPublished ?? true,
      openDate: t.openDate ?? null, deadline: t.deadline,
      applicationCount: db.applications.filter((a) => a.formTemplateId === t.id).length,
    });
  }

  async getAllApplications(
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedApplicationResponse> {
    const all = db.applications.map(toAdminApplication);
    const start = (page - 1) * limit;
    return delay({
      applications: all.slice(start, start + limit),
      pagination: paginate(all.length, page, limit),
    });
  }

  async getApplicationById(id: string): Promise<ApplicationWithAdminData> {
    const app = findApp(id);
    if (!app) {
      throw new AdminServiceError(`Aplicación ${id} no encontrada`, 404);
    }
    return delay(toAdminApplication(app));
  }

  async getApplicationsByStatus(
    status: string | string[],
    page: number = 1,
    limit: number = 20,
    filters?: any,
    formTemplateId?: string | null
  ): Promise<PaginatedApplicationResponse> {
    const statuses = Array.isArray(status)
      ? status
      : String(status).split(',').map((s) => s.trim());

    let apps = this.appsForTemplate(formTemplateId).filter((a) => statuses.includes(a.status));

    if (filters?.unidadAcademica) {
      apps = apps.filter(
        (a) => findUser(a.userId)?.userProfile?.unidadAcademica === filters.unidadAcademica
      );
    }
    if (filters?.researcherType) {
      apps = apps.filter(
        (a) => findUser(a.userId)?.userProfile?.researcherType === filters.researcherType
      );
    }
    if (filters?.search) {
      const q = String(filters.search).toLowerCase();
      apps = apps.filter((a) => {
        const u = findUser(a.userId);
        const title = a.sectionAnswers?.PROJECT_INFORMATION?.projectTitle ?? '';
        const keywords = String(a.sectionAnswers?.PROJECT_INFORMATION?.keywords ?? '').toLowerCase();
        return (
          a.applicationNumber.toLowerCase().includes(q) ||
          (u?.name ?? '').toLowerCase().includes(q) ||
          (u?.email ?? '').toLowerCase().includes(q) ||
          ((u?.userProfile as any)?.run ?? '').toLowerCase().includes(q) ||
          ((u?.userProfile as any)?.unidadAcademica ?? '').toLowerCase().includes(q) ||
          title.toLowerCase().includes(q) ||
          keywords.includes(q)
        );
      });
    }
    if (filters?.evaluatorStatus) {
      apps = apps.filter((a) => {
        const hasEvaluators = db.assignments.some((as) => as.applicationId === a.id);
        return filters.evaluatorStatus === 'assigned' ? hasEvaluators : !hasEvaluators;
      });
    }

    const mapped = apps.map(toAdminApplication);
    const start = (page - 1) * limit;
    return delay({
      applications: mapped.slice(start, start + limit),
      pagination: paginate(mapped.length, page, limit),
    });
  }

  async updateApplicationStatus(
    applicationId: string,
    newStatus: AdminApplicationStatus,
    notes?: string
  ): Promise<ApplicationWithAdminData> {
    const app = findApp(applicationId);
    if (!app) throw new AdminServiceError('Aplicación no encontrada', 404);
    pushHistory(app, newStatus, notes);
    persist();
    return delay(toAdminApplication(app), 200);
  }

  async assignReviewer(
    applicationId: string,
    adminId: string,
    adminName: string
  ): Promise<ApplicationWithAdminData> {
    const app = findApp(applicationId);
    if (!app) throw new AdminServiceError('Aplicación no encontrada', 404);
    app.reviewerAssignment = {
      adminId,
      adminName,
      assignedAt: new Date().toISOString(),
    };
    pushHistory(app, 'under_admissibility_review', 'Revisor asignado.');
    persist();
    return delay(toAdminApplication(app), 200);
  }

  async markAdmissible(
    applicationId: string,
    _adminId: string,
    _adminName: string,
    notes?: string
  ): Promise<ApplicationWithAdminData> {
    const app = findApp(applicationId);
    if (!app) throw new AdminServiceError('Aplicación no encontrada', 404);
    pushHistory(app, 'admissible', notes);
    persist();
    return delay(toAdminApplication(app), 200);
  }

  async markNotAdmissible(
    applicationId: string,
    _adminId: string,
    _adminName: string,
    notes?: string
  ): Promise<ApplicationWithAdminData> {
    const app = findApp(applicationId);
    if (!app) throw new AdminServiceError('Aplicación no encontrada', 404);
    pushHistory(app, 'not_admissible', notes);
    persist();
    return delay(toAdminApplication(app), 200);
  }

  async markEvaluated(
    applicationId: string,
    notes?: string
  ): Promise<ApplicationWithAdminData> {
    const app = findApp(applicationId);
    if (!app) throw new AdminServiceError('Aplicación no encontrada', 404);
    pushHistory(app, 'evaluated', notes);
    persist();
    return delay(toAdminApplication(app), 200);
  }

  async getFilterOptions(formTemplateId?: string | null): Promise<{
    unidadAcademica: string[];
    researcherType: string[];
    evaluators: { value: string; label: string }[];
    oecdAreas: string[];
  }> {
    const scopedApps = this.appsForTemplate(formTemplateId);
    const unidadAcademica = Array.from(
      new Set(
        scopedApps
          .map((a) => findUser(a.userId)?.userProfile?.unidadAcademica)
          .filter((v): v is string => !!v)
      )
    ).sort();
    const evaluators = db.users
      .filter((u) => u.role === 'evaluator')
      .map((u) => ({ value: u.id, label: u.name }));
    const oecdAreas = Array.from(
      new Set(
        scopedApps
          .map((a) => a.sectionAnswers?.PROJECT_INFORMATION?.oecdArea)
          .filter((v): v is string => !!v)
      )
    ).sort();

    return delay({
      unidadAcademica,
      researcherType: ['consolidated', 'emerging'],
      evaluators,
      oecdAreas,
    });
  }

  async revertApplicationStatus(
    applicationId: string,
    notes?: string
  ): Promise<ApplicationWithAdminData> {
    const app = findApp(applicationId);
    if (!app) throw new AdminServiceError('Aplicación no encontrada', 404);
    if (app.statusHistory.length > 1) {
      app.statusHistory = app.statusHistory.slice(0, -1);
      const previous = app.statusHistory[app.statusHistory.length - 1];
      app.status = previous.status;
    } else {
      app.status = 'submitted';
      app.statusHistory = [];
    }
    app.updatedAt = new Date().toISOString();
    if (notes) {
      app.statusHistory.push({
        status: app.status,
        changedBy: '33333333-3333-4333-8333-000000000003',
        changedByName: 'Admin DINV',
        changedAt: app.updatedAt,
        notes,
      });
    }
    persist();
    return delay(toAdminApplication(app), 200);
  }

  async getApplicationEvaluations(
    applicationId: string
  ): Promise<ApplicationEvaluationsResponseDto> {
    const app = findApp(applicationId);
    if (!app) throw new AdminServiceError('Aplicación no encontrada', 404);

    const assignments = db.assignments.filter((a) => a.applicationId === applicationId);

    // Identify arbiters: active evaluators beyond the first 2 (sorted by assignedAt).
    // E1 and E2 are the primary evaluators; E3+ are brought in due to score deviation.
    const activeAssignmentsSorted = assignments
      .filter((a) => a.status !== 'declined' && a.status !== 'conflict_declared')
      .sort((a, b) => new Date(a.assignedAt).getTime() - new Date(b.assignedAt).getTime());
    const arbiterAssignmentIds = new Set(
      activeAssignmentsSorted.length > 2
        ? activeAssignmentsSorted.slice(2).map((a) => a.id)
        : []
    );

    const evaluations: EvaluationListItemDto[] = assignments.map((a) => {
      const ev = db.evaluations.find((e) => e.assignmentId === a.id);
      const evaluator = findUser(a.evaluatorId);
      const criteriaScores: any = {};
      CRITERIA_KEYS.forEach((k) => {
        criteriaScores[k] = ev?.criteriaEvaluations[k]?.score ?? null;
      });
      return {
        id: ev?.id ?? a.id,
        evaluatorName: evaluator?.name ?? 'Evaluador',
        updatedAt: ev?.updatedAt ?? a.assignedAt,
        status: ev?.submittedAt ? 'submitted' : 'pending',
        assignmentStatus: a.status,
        isArbiter: arbiterAssignmentIds.has(a.id),
        finalScore: (ev && ev.submittedAt) ? weightedScore(ev.criteriaEvaluations) : null,
        conflictDetails: a.conflictDetails ?? null,
        criteriaScores,
      };
    });

    return delay({
      applicationId,
      applicationNumber: app.applicationNumber,
      summary: buildEvaluationSummary(applicationId),
      evaluations,
      maximumCriteriaScoreDifference: 2,
      maximumScoreDifference: 1,
      minimumEvaluationsRequired: 2,
    });
  }

  async getEvaluationSummary(
    evaluationId: string
  ): Promise<SingleEvaluationResponseDto> {
    const ev = db.evaluations.find((e) => e.id === evaluationId);
    if (!ev) throw new AdminServiceError('Evaluación no encontrada', 404);
    const assignment = db.assignments.find((a) => a.id === ev.assignmentId);
    const app = findApp(ev.applicationId);
    const evaluator = findUser(ev.evaluatorId);
    const applicant = findUser(app?.userId ?? '');

    const criteriaScores: Record<string, number> = {};
    CRITERIA_KEYS.forEach((k) => {
      criteriaScores[k] = ev.criteriaEvaluations[k]?.score ?? 0;
    });

    return delay({
      id: ev.id,
      assignmentId: ev.assignmentId,
      criteriaEvaluations: ev.criteriaEvaluations as any,
      budgetComments: ev.budgetComments,
      overallComments: ev.overallComments,
      conflictsDeclared: ev.conflictsDeclared,
      conflictDetails: ev.conflictDetails,
      submittedAt: ev.submittedAt,
      createdAt: ev.createdAt,
      updatedAt: ev.updatedAt,
      scoreSummary: {
        finalScore: weightedScore(ev.criteriaEvaluations),
        criteriaScores,
        isValid: true,
      },
      application: {
        id: app?.id ?? '',
        applicationNumber: app?.applicationNumber ?? '',
        status: app?.status ?? '',
        answers: { sectionAnswers: app?.sectionAnswers, sectionStatus: app?.sectionStatus },
        user: {
          id: applicant?.id ?? '',
          name: applicant?.name ?? 'Usuario',
          email: applicant?.email ?? '',
        },
      },
      evaluator: {
        id: evaluator?.id ?? '',
        name: evaluator?.name ?? 'Evaluador',
        email: evaluator?.email ?? '',
        isExternal: evaluator?.isExternal ?? false,
      },
      assignment: {
        id: assignment?.id ?? '',
        assignedAt: assignment?.assignedAt ?? '',
        status: assignment?.status ?? '',
        conflictDeclared: assignment?.conflictDeclared ?? false,
        conflictStatus: assignment?.conflictStatus ?? null,
      },
      finalScore: weightedScore(ev.criteriaEvaluations),
    });
  }

  async exportApplications(filters?: {
    statuses?: string[];
    academicUnits?: string[];
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
  }): Promise<void> {
    await delay(null, 300);
    const header = 'application_number,status,applicant,unidad_academica\n';
    let apps = db.applications;
    if (filters?.statuses && filters.statuses.length > 0) {
      apps = apps.filter((a) => filters.statuses!.includes(a.status));
    } else if (filters?.status) {
      apps = apps.filter((a) => a.status === filters.status);
    }
    if (filters?.academicUnits && filters.academicUnits.length > 0) {
      apps = apps.filter((a) => {
        const u = findUser(a.userId);
        return filters.academicUnits!.includes(u?.userProfile?.unidadAcademica ?? '');
      });
    }
    const rows = apps
      .map((a) => {
        const u = findUser(a.userId);
        return [
          a.applicationNumber,
          a.status,
          u?.name ?? '',
          u?.userProfile?.unidadAcademica ?? '',
        ].join(',');
      })
      .join('\n');
    triggerDownload(header + rows, 'applications-export.csv', 'text/csv');
  }

  async exportEvaluations(): Promise<void> {
    await delay(null, 300);
    const header = 'evaluation_id,application,evaluator,final_score\n';
    const rows = db.evaluations
      .map((e) => {
        const app = findApp(e.applicationId);
        const evaluator = findUser(e.evaluatorId);
        return [
          e.id,
          app?.applicationNumber ?? '',
          evaluator?.name ?? '',
          weightedScore(e.criteriaEvaluations),
        ].join(',');
      })
      .join('\n');
    triggerDownload(header + rows, 'evaluations-export.csv', 'text/csv');
  }

  // Evaluator Management Methods

  async getAvailableEvaluators(templateId?: string): Promise<Evaluator[]> {
    const excludedIds = templateId
      ? new Set(
          db.contestExclusions
            .filter((e) => e.templateId === templateId)
            .map((e) => e.evaluatorId)
        )
      : new Set<string>();
    const evaluators = db.users
      .filter((u) => u.role === 'evaluator' && !excludedIds.has(u.id))
      .map(toEvaluator);
    return delay(evaluators);
  }

  async getContestExclusions(templateId: string): Promise<string[]> {
    return delay(
      (db.contestExclusions ?? [])
        .filter((e) => e.templateId === templateId)
        .map((e) => e.evaluatorId)
    );
  }

  async excludeEvaluatorFromContest(templateId: string, evaluatorId: string): Promise<void> {
    if (!db.contestExclusions) (db as any).contestExclusions = [];
    if (!db.contestExclusions.find((e) => e.templateId === templateId && e.evaluatorId === evaluatorId)) {
      db.contestExclusions.push({ templateId, evaluatorId });
      persist();
    }
    return delay(undefined as any);
  }

  async includeEvaluatorInContest(templateId: string, evaluatorId: string): Promise<void> {
    if (!db.contestExclusions) return delay(undefined as any);
    const idx = db.contestExclusions.findIndex(
      (e) => e.templateId === templateId && e.evaluatorId === evaluatorId
    );
    if (idx !== -1) {
      db.contestExclusions.splice(idx, 1);
      persist();
    }
    return delay(undefined as any);
  }

  async getApplicationEvaluators(
    applicationId: string
  ): Promise<EvaluatorAssignment[]> {
    const assignments = db.assignments
      .filter((a) => a.applicationId === applicationId)
      .map(toEvaluatorAssignment);
    return delay(assignments);
  }

  async assignEvaluator(
    applicationId: string,
    request: AssignEvaluatorRequest
  ): Promise<EvaluatorAssignment> {
    let evaluatorId = request.evaluatorId;

    // External evaluator by email — create the user on the fly.
    if (!evaluatorId && request.evaluatorEmail) {
      const existing = db.users.find(
        (u) => u.email.toLowerCase() === request.evaluatorEmail!.toLowerCase()
      );
      if (existing) {
        evaluatorId = existing.id;
      } else {
        evaluatorId = newId('user');
        db.users.push({
          id: evaluatorId,
          email: request.evaluatorEmail,
          name: request.evaluatorName ?? request.evaluatorEmail,
          role: 'evaluator',
          isActive: true,
          isExternal: true,
          isConfirmed: false,
          createdAt: new Date().toISOString(),
        });
      }
    }

    if (!evaluatorId) {
      throw new AdminServiceError('Debe indicar un evaluador', 400);
    }

    const assignment = {
      id: newId('asg'),
      applicationId,
      evaluatorId,
      assignedBy: '33333333-3333-4333-8333-000000000003',
      assignedAt: new Date().toISOString(),
      status: 'pending' as const,
      // "Asignar" no manda correo: queda pendiente de invitar, visible en
      // Evaluaciones por evaluador. La vía que sí notifica es inviteEvaluator.
      invitationSentAt: null,
      evaluationSubmittedAt: null,
      conflictDeclared: false,
      conflictStatus: null,
      conflictDetails: null,
      conflictDeclaredAt: null,
    };
    db.assignments.push(assignment);
    persist();
    return delay(toEvaluatorAssignment(assignment), 200);
  }

  async removeEvaluatorAssignment(
    applicationId: string,
    evaluatorId: string
  ): Promise<{ message: string }> {
    const idx = db.assignments.findIndex(
      (a) => a.applicationId === applicationId && a.evaluatorId === evaluatorId
    );
    if (idx !== -1) {
      const [removed] = db.assignments.splice(idx, 1);
      const evalIdx = db.evaluations.findIndex((e) => e.assignmentId === removed.id);
      if (evalIdx !== -1) db.evaluations.splice(evalIdx, 1);
      persist();
    }
    return delay({ message: 'Asignación de evaluador removida.' });
  }

  async inviteEvaluator(
    request: InviteEvaluatorRequest
  ): Promise<InviteEvaluatorResponse> {
    const existing = db.users.find(
      (u) => u.email.toLowerCase() === request.email.toLowerCase()
    );
    let evaluatorId: string;
    let isNewUser = false;

    if (existing) {
      evaluatorId = existing.id;
    } else {
      isNewUser = true;
      evaluatorId = newId('user');
      db.users.push({
        id: evaluatorId,
        email: request.email,
        name: request.name,
        role: 'evaluator',
        isActive: true,
        isExternal: true,
        isConfirmed: false,
        createdAt: new Date().toISOString(),
      });
    }

    let assignmentId: string | undefined;
    if (request.applicationId) {
      const now = new Date().toISOString();
      const assignment = {
        id: newId('asg'),
        applicationId: request.applicationId,
        evaluatorId,
        assignedBy: '33333333-3333-4333-8333-000000000003',
        assignedAt: now,
        status: 'pending' as const,
        // Este camino ("Invitar y Asignar") sí envía el correo.
        invitationSentAt: now,
        evaluationSubmittedAt: null,
        conflictDeclared: false,
        conflictStatus: null,
        conflictDetails: null,
        conflictDeclaredAt: null,
      };
      db.assignments.push(assignment);
      assignmentId = assignment.id;
    }
    persist();

    return delay({
      evaluatorId,
      email: request.email,
      name: request.name,
      isNewUser,
      isExternal: true,
      roleUpdated: false,
      assignmentId,
      emailSent: true,
      message: 'Invitación enviada (simulada).',
    });
  }

  // User Management Methods

  async getAllUsers(filters?: AdminUserFilters): Promise<AdminUsersResponse> {
    let users = [...db.users];

    if (filters?.role) users = users.filter((u) => u.role === filters.role);
    if (filters?.isActive !== undefined)
      users = users.filter((u) => u.isActive === filters.isActive);
    if (filters?.isExternal !== undefined)
      users = users.filter((u) => (u as any).isExternal === filters.isExternal);
    if (filters?.unidadAcademica)
      users = users.filter(
        (u) => u.userProfile?.unidadAcademica === filters.unidadAcademica
      );
    if (filters?.researcherType)
      users = users.filter(
        (u) => u.userProfile?.researcherType === filters.researcherType
      );
    if (filters?.search) {
      const q = filters.search.toLowerCase();
      users = users.filter(
        (u) =>
          u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      );
    }

    const page = filters?.page ?? 1;
    const limit = filters?.limit ?? 20;
    const start = (page - 1) * limit;
    return delay({
      users: users.slice(start, start + limit),
      pagination: paginate(users.length, page, limit),
    });
  }

  async getUserById(id: string): Promise<User> {
    const user = findUser(id);
    if (!user) throw new AdminServiceError(`Usuario ${id} no encontrado`, 404);
    return delay(user);
  }

  async updateUser(id: string, data: any): Promise<User> {
    const user = findUser(id);
    if (!user) throw new AdminServiceError(`Usuario ${id} no encontrado`, 404);
    if (data.name !== undefined) user.name = data.name;
    if (data.email !== undefined) user.email = data.email;
    if (data.role !== undefined) user.role = data.role;
    if (data.isActive !== undefined) user.isActive = data.isActive;
    if (data.profile) {
      user.userProfile = { ...(user.userProfile ?? {}), ...data.profile };
    }
    persist();
    return delay(user, 200);
  }

  async createUser(data: any): Promise<User> {
    const user = {
      id: newId('user'),
      email: data.email,
      name: data.name,
      role: data.role ?? 'researcher',
      isActive: data.isActive ?? true,
      isExternal: data.isExternal ?? false,
      isConfirmed: false,
      createdAt: new Date().toISOString(),
      userProfile: data.profile,
    };
    db.users.push(user);
    persist();
    return delay(user, 200);
  }

  // Application Alerts Methods

  async getAlerts(
    filters: AlertFilters = {},
    page: number = 1,
    limit: number = 10
  ): Promise<PaginatedAlertsResponse> {
    let alerts = [...db.alerts];
    if (filters.status) alerts = alerts.filter((a) => a.status === filters.status);
    if (filters.alertTypes?.length)
      alerts = alerts.filter((a) => filters.alertTypes!.includes(a.alertType as AlertType));
    if (filters.applicationId)
      alerts = alerts.filter((a) => a.applicationId === filters.applicationId);
    if (filters.evaluatorId)
      alerts = alerts.filter((a) => a.evaluatorId === filters.evaluatorId);
    if (filters.priority)
      alerts = alerts.filter((a) => a.priority === filters.priority);

    // Enrich with application + evaluator summaries for display.
    const data = alerts.map((a) => {
      const app = a.applicationId ? findApp(a.applicationId) : undefined;
      const appUser = app ? findUser(app.userId) : undefined;
      const evUser = a.evaluatorId ? findUser(a.evaluatorId) : undefined;
      return {
        ...a,
        application: app
          ? {
              id: app.id,
              applicationNumber: app.applicationNumber,
              status: app.status,
              user: {
                id: appUser?.id ?? '',
                name: appUser?.name ?? 'Usuario',
                email: appUser?.email ?? '',
              },
            }
          : null,
        evaluator: evUser
          ? { id: evUser.id, name: evUser.name, email: evUser.email }
          : null,
      } as ApplicationAlert;
    });

    // Sort — default: createdAt ASC (oldest first)
    const PRIORITY_RANK: Record<string, number> = { low: 0, medium: 1, high: 2, urgent: 3 };
    const sortBy = filters.sortBy ?? 'createdAt';
    const sortDir = filters.sortDir ?? 'asc';
    data.sort((a, b) => {
      let av: any, bv: any;
      switch (sortBy) {
        case 'alertType': av = a.alertType; bv = b.alertType; break;
        case 'priority':
          av = PRIORITY_RANK[a.priority as string] ?? 0;
          bv = PRIORITY_RANK[b.priority as string] ?? 0;
          break;
        case 'status': av = a.status; bv = b.status; break;
        case 'title': av = a.title; bv = b.title; break;
        case 'origin':
          av = a.application?.applicationNumber ?? a.evaluator?.name ?? '';
          bv = b.application?.applicationNumber ?? b.evaluator?.name ?? '';
          break;
        case 'createdAt':
        default:
          av = new Date(a.createdAt).getTime();
          bv = new Date(b.createdAt).getTime();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    const start = (page - 1) * limit;
    const total = data.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    return delay({
      data: data.slice(start, start + limit),
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    });
  }

  async resolveAlert(
    alertId: string,
    resolutionNote?: string
  ): Promise<ApplicationAlert> {
    const alert = db.alerts.find((a) => a.id === alertId);
    if (!alert) throw new AdminServiceError('Alerta no encontrada', 404);
    alert.status = 'resolved' as any;
    alert.resolvedAt = new Date();
    alert.resolvedBy = '33333333-3333-4333-8333-000000000003';
    alert.resolutionNote = resolutionNote ?? null;
    persist();
    return delay(alert, 200);
  }

  async dismissAlert(alertId: string, note: string): Promise<ApplicationAlert> {
    const alert = db.alerts.find((a) => a.id === alertId);
    if (!alert) throw new AdminServiceError('Alerta no encontrada', 404);
    alert.status = 'dismissed' as any;
    alert.resolvedAt = new Date();
    alert.resolvedBy = '33333333-3333-4333-8333-000000000003';
    alert.dismissNote = note;
    persist();
    return delay(alert, 200);
  }

  async getAlertStats(): Promise<{
    openByType: Partial<Record<AlertType, number>>;
    totalOpen: number;
    totalResolved: number;
    totalDismissed: number;
  }> {
    const openAlerts = db.alerts.filter((a) => a.status === AlertStatus.OPEN);
    const openByType: Partial<Record<AlertType, number>> = {};
    for (const a of openAlerts) {
      const t = a.alertType as AlertType;
      openByType[t] = (openByType[t] ?? 0) + 1;
    }
    return delay({
      openByType,
      totalOpen: openAlerts.length,
      totalResolved: db.alerts.filter((a) => a.status === AlertStatus.RESOLVED).length,
      totalDismissed: db.alerts.filter((a) => a.status === AlertStatus.DISMISSED).length,
    });
  }

  async getEvaluatorStats(formTemplateId?: string | null): Promise<{
    total: number;
    completed: number;
    inProgress: number;
    notStarted: number;
    conflictDeclared: number;
    declined: number;
    noConflict: number;
    pendingDeclaration: number;
  }> {
    const appIds = this.appIdsForTemplate(formTemplateId);
    const all = formTemplateId
      ? db.assignments.filter((a) => appIds.has(a.applicationId))
      : db.assignments;
    return delay({
      total: all.length,
      completed: all.filter((a) => a.status === 'completed').length,
      inProgress: all.filter((a) => a.status === 'in_progress').length,
      notStarted: all.filter((a) => a.status === 'pending' || a.status === 'accepted_not_started').length,
      conflictDeclared: all.filter((a) => a.status === 'conflict_declared').length,
      declined: all.filter((a) => a.status === 'declined').length,
      noConflict: all.filter((a) => a.conflictStatus === 'no_conflict').length,
      pendingDeclaration: all.filter((a) => a.conflictStatus === null).length,
    });
  }

  async getTopUAs(formTemplateId?: string | null): Promise<Array<{ name: string; count: number }>> {
    const counts: Record<string, number> = {};
    for (const app of this.appsForTemplate(formTemplateId)) {
      const ua = findUser(app.userId)?.userProfile?.unidadAcademica ?? '';
      if (ua) counts[ua] = (counts[ua] || 0) + 1;
    }
    return delay(
      Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ name, count }))
    );
  }

  async getUAsByFaculty(formTemplateId?: string | null): Promise<
    Array<{ faculty: string; total: number; units: Array<{ name: string; count: number }> }>
  > {
    const counts: Record<string, Record<string, number>> = {};
    for (const app of this.appsForTemplate(formTemplateId)) {
      const profile = findUser(app.userId)?.userProfile;
      const ua = profile?.unidadAcademica ?? '';
      if (!ua) continue;
      const faculty = profile?.facultadUC ?? 'Sin facultad';
      if (!counts[faculty]) counts[faculty] = {};
      counts[faculty][ua] = (counts[faculty][ua] || 0) + 1;
    }
    const result = Object.entries(counts)
      .map(([faculty, uas]) => {
        const units = Object.entries(uas)
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => ({ name, count }));
        return { faculty, total: units.reduce((s, u) => s + u.count, 0), units };
      })
      .sort((a, b) => b.total - a.total);
    return delay(result);
  }

  async createAlert(data: {
    applicationNumber: string;
    alertType: AlertType;
    title: string;
    description: string;
    priority?: AlertPriority;
  }): Promise<ApplicationAlert> {
    const app = db.applications.find(
      (a) => a.applicationNumber === data.applicationNumber
    );
    if (!app) {
      throw new AdminServiceError(
        `No se encontró la postulación con folio ${data.applicationNumber}.`,
        404
      );
    }
    const duplicate = db.alerts.find(
      (a) =>
        a.applicationId === app.id &&
        a.alertType === data.alertType &&
        a.status === AlertStatus.OPEN
    );
    if (duplicate) {
      throw new AdminServiceError(
        'Ya existe una alerta abierta de este tipo para esta postulación.',
        409
      );
    }
    const catalogEntry = ALERT_CATALOG.find((e) => e.type === data.alertType);
    const newAlert = {
      id: newId('alert'),
      applicationId: app.id,
      alertType: data.alertType as any,
      title: data.title,
      description: data.description,
      priority: (data.priority ?? catalogEntry?.defaultPriority ?? AlertPriority.MEDIUM) as any,
      status: AlertStatus.OPEN as any,
      createdAt: new Date(),
      resolvedAt: null,
      resolvedBy: null,
      resolutionNote: null,
      metadata: { createdManually: true },
    };
    db.alerts.push(newAlert);
    persist();
    const user = findUser(app.userId);
    return delay({
      ...newAlert,
      application: {
        id: app.id,
        applicationNumber: app.applicationNumber,
        status: app.status,
        user: { id: user?.id ?? '', name: user?.name ?? '', email: user?.email ?? '' },
      },
    } as ApplicationAlert, 200);
  }

  // Internal helper — creates an alert if no open duplicate exists (by type + anchor key).
  private _autoCreateAlert(fields: {
    alertType: AlertType;
    title: string;
    description: string;
    priority?: AlertPriority;
    applicationId?: string | null;
    evaluatorId?: string | null;
    assignmentId?: string | null;
  }): void {
    const catalogEntry = ALERT_CATALOG.find((e) => e.type === fields.alertType);
    const priority = fields.priority ?? catalogEntry?.defaultPriority ?? AlertPriority.MEDIUM;
    const dedup = db.alerts.find(
      (a) =>
        a.alertType === fields.alertType &&
        a.status === AlertStatus.OPEN &&
        (fields.assignmentId ? a.assignmentId === fields.assignmentId
          : fields.evaluatorId && !fields.assignmentId ? a.evaluatorId === fields.evaluatorId && !a.assignmentId
          : a.applicationId === fields.applicationId)
    );
    if (dedup) return;
    db.alerts.push({
      id: newId('alert'),
      alertType: fields.alertType as any,
      title: fields.title,
      description: fields.description,
      priority: priority as any,
      status: AlertStatus.OPEN as any,
      applicationId: fields.applicationId ?? null,
      evaluatorId: fields.evaluatorId ?? null,
      assignmentId: fields.assignmentId ?? null,
      createdAt: new Date(),
      resolvedAt: null,
      resolvedBy: null,
      resolutionNote: null,
      metadata: { autoGenerated: true },
    } as any);
  }

  // Auto-resolve alerts whose condition is no longer met.
  private _autoResolveAlert(
    alertType: AlertType,
    anchorKey: { assignmentId?: string; evaluatorId?: string; applicationId?: string }
  ): void {
    const alert = db.alerts.find(
      (a) =>
        a.alertType === alertType &&
        a.status === AlertStatus.OPEN &&
        (anchorKey.assignmentId
          ? a.assignmentId === anchorKey.assignmentId
          : anchorKey.evaluatorId && !anchorKey.assignmentId
          ? a.evaluatorId === anchorKey.evaluatorId && !a.assignmentId
          : a.applicationId === anchorKey.applicationId)
    );
    if (!alert) return;
    alert.status = AlertStatus.RESOLVED as any;
    alert.resolvedAt = new Date();
    alert.resolvedBy = 'system';
    alert.resolutionNote = 'Resuelta automáticamente: la condición que originó la alerta ya no está activa.';
  }

  // Simulates what a scheduled job would do: scan for alert conditions and fire/resolve.
  async checkAndGenerateAlerts(): Promise<void> {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const {
      INVITATION_RESPONSE_DAYS,
      EVALUATOR_LOGIN_DAYS,
      OVERLOAD_THRESHOLD,
      EVALUATION_DELAYED_MEDIUM_DAYS,
      EVALUATION_DELAYED_HIGH_DAYS,
    } = ALERT_CONFIG;

    for (const asg of db.assignments) {
      const evaluator = findUser(asg.evaluatorId);
      const app = findApp(asg.applicationId);
      if (!evaluator || !app) continue;

      const daysSinceAssigned = (now - new Date(asg.assignedAt).getTime()) / DAY_MS;

      // INVITATION_NO_RESPONSE — pending (hasn't responded) past threshold.
      // Se cuenta desde el ENVÍO, no desde la asignación: una asignación creada
      // sin notificar (importación con el switch apagado) no puede generar una
      // alerta que culpa al evaluador de no responder algo que nunca recibió.
      const daysSinceInvited = asg.invitationSentAt
        ? (now - new Date(asg.invitationSentAt).getTime()) / DAY_MS
        : null;
      if (
        asg.status === 'pending' &&
        daysSinceInvited !== null &&
        daysSinceInvited >= INVITATION_RESPONSE_DAYS
      ) {
        this._autoCreateAlert({
          alertType: AlertType.INVITATION_NO_RESPONSE,
          title: 'Invitación sin respuesta',
          description: `${evaluator.name} no ha respondido a la invitación en más de ${INVITATION_RESPONSE_DAYS} días.`,
          priority: AlertPriority.MEDIUM,
          applicationId: asg.applicationId,
          evaluatorId: asg.evaluatorId,
          assignmentId: asg.id,
        });
      }

      // EVALUATION_DELAYED — accepted but not submitted past threshold:
      //   ≥ EVALUATION_DELAYED_MEDIUM_DAYS (11) → MEDIUM
      //   ≥ EVALUATION_DELAYED_HIGH_DAYS   (14) → HIGH
      if (
        (asg.status === 'accepted_not_started' || asg.status === 'in_progress') &&
        daysSinceAssigned >= EVALUATION_DELAYED_MEDIUM_DAYS
      ) {
        const priority = daysSinceAssigned >= EVALUATION_DELAYED_HIGH_DAYS
          ? AlertPriority.HIGH
          : AlertPriority.MEDIUM;
        this._autoCreateAlert({
          alertType: AlertType.EVALUATION_DELAYED,
          title: 'Evaluación no completada',
          description: `${evaluator.name} lleva más de ${Math.floor(daysSinceAssigned)} días sin completar la evaluación.`,
          priority,
          applicationId: asg.applicationId,
          evaluatorId: asg.evaluatorId,
          assignmentId: asg.id,
        });
      }

      // INVITATION_REJECTED — conflict_declared
      if (asg.status === 'conflict_declared') {
        this._autoCreateAlert({
          alertType: AlertType.INVITATION_REJECTED,
          title: 'Evaluador declaró conflicto de interés',
          description: `${evaluator.name} declaró conflicto de interés para la postulación ${app.applicationNumber}.`,
          priority: AlertPriority.HIGH,
          applicationId: asg.applicationId,
          evaluatorId: asg.evaluatorId,
          assignmentId: asg.id,
        });
      }

      // Auto-resolve INVITATION_NO_RESPONSE once the evaluator has responded (no longer pending)
      if (asg.status !== 'pending') {
        this._autoResolveAlert(AlertType.INVITATION_NO_RESPONSE, { assignmentId: asg.id });
      }
      // Auto-resolve EVALUATION_DELAYED when completed
      if (asg.status === 'completed') {
        this._autoResolveAlert(AlertType.EVALUATION_DELAYED, { assignmentId: asg.id });
      }
    }

    // EVALUATOR_NO_LOGIN / EVALUATOR_OVERLOADED — per evaluator
    const evaluators = db.users.filter((u) => u.role === 'evaluator');
    for (const ev of evaluators) {
      const activeAssignments = db.assignments.filter(
        (a) => a.evaluatorId === ev.id &&
          (a.status === 'pending' || a.status === 'accepted_not_started' || a.status === 'in_progress')
      );

      // EVALUATOR_NO_LOGIN — no platform activity for threshold days AND has active assignments.
      //
      // PROTOTYPE: lastActivityAt = max(lastLoginAt, last submitted evaluation for this evaluator).
      // This approximates "any meaningful action on the platform."
      //
      // PRODUCTION (DEV NOTE — 2026-06-26):
      //   Replace with a single `user.lastActivityAt` field on the User model, updated by an
      //   API middleware on every authenticated request (e.g. Express/NestJS interceptor that
      //   writes `user.lastActivityAt = new Date()` after each successful request). This covers
      //   all activity — browsing applications, downloading PDFs, navigating screens — without
      //   needing to track individual events. Both this alert AND the "Últ. acceso" column in
      //   getEvaluatorDashboard() must read the same field for consistent behavior.
      //   Threshold constant: ALERT_CONFIG.EVALUATOR_LOGIN_DAYS (rename to EVALUATOR_INACTIVE_DAYS).
      const lastLoginTs = (ev as any).lastLoginAt ? new Date((ev as any).lastLoginAt).getTime() : 0;
      // Use ALL assignments (not just active) so completed evaluations count as recent activity
      const allAsgIds = new Set(db.assignments.filter((a) => a.evaluatorId === ev.id).map((a) => a.id));
      const lastEvalTs = (db as any).evaluations
        .filter((e: any) => allAsgIds.has(e.assignmentId) && e.submittedAt)
        .reduce((max: number, e: any) => Math.max(max, new Date(e.submittedAt).getTime()), 0);
      const lastActivityTs = Math.max(lastLoginTs, lastEvalTs);
      const daysSinceActivity = lastActivityTs > 0 ? (now - lastActivityTs) / DAY_MS : Infinity;
      if (daysSinceActivity >= EVALUATOR_LOGIN_DAYS && activeAssignments.length > 0) {
        this._autoCreateAlert({
          alertType: AlertType.EVALUATOR_NO_LOGIN,
          title: 'Evaluador sin actividad en la plataforma',
          description: isFinite(daysSinceActivity)
            ? `${ev.name} no ha tenido actividad en ${Math.floor(daysSinceActivity)} días y tiene evaluaciones pendientes.`
            : `${ev.name} nunca ha tenido actividad en la plataforma y tiene evaluaciones pendientes.`,
          priority: AlertPriority.HIGH,
          evaluatorId: ev.id,
        });
      } else {
        this._autoResolveAlert(AlertType.EVALUATOR_NO_LOGIN, { evaluatorId: ev.id });
      }

      // EVALUATOR_OVERLOADED — active assignments exceed threshold
      if (activeAssignments.length > OVERLOAD_THRESHOLD) {
        this._autoCreateAlert({
          alertType: AlertType.EVALUATOR_OVERLOADED,
          title: 'Evaluador con sobrecarga de asignaciones',
          description: `${ev.name} tiene ${activeAssignments.length} asignaciones activas, superando el límite recomendado de ${OVERLOAD_THRESHOLD}.`,
          priority: AlertPriority.MEDIUM,
          evaluatorId: ev.id,
        });
      } else {
        this._autoResolveAlert(AlertType.EVALUATOR_OVERLOADED, { evaluatorId: ev.id });
      }
    }

    // -------------------------------------------------------------------------
    // FASE 3 — ESCALACIÓN DE PRIORIDAD (pendiente de implementación)
    //
    // Buscar alertas abiertas cuya condición haya empeorado y subir su prioridad.
    // Para implementar: iterar db.alerts donde status === OPEN, recalcular la
    // prioridad según días transcurridos y actualizar alert.priority + persist().
    //
    // EVALUATION_DELAYED: escalation already implemented in checkAndGenerateAlerts()
    //   ≥ EVALUATION_DELAYED_MEDIUM_DAYS (11) → MEDIUM
    //   ≥ EVALUATION_DELAYED_HIGH_DAYS   (14) → HIGH
    //
    // DEADLINE_APPROACHING (requiere campo deadline en db.formTemplates):
    //   daysUntilDeadline <= 1  →  subir a HIGH
    //   daysUntilDeadline <= 7  →  crear/mantener MEDIUM (ya implementado en seed)
    //
    // EVALUATOR_OVERLOADED:
    //   activeAssignments.length >= OVERLOAD_THRESHOLD + 2  →  subir a MEDIUM
    //   (actualmente siempre LOW)
    //
    // Referencia: ADM-042 Fase 3, columna "Prioridad def." de la spec de alertas.
    // -------------------------------------------------------------------------

    persist();
  }

  // ─── Tableros REP-001 (visual dashboards) ─────────────────────────────────

  async getOecdDistribution(formTemplateId?: string | null): Promise<Array<{ area: string; count: number }>> {
    const counts: Record<string, number> = {};
    for (const app of this.appsForTemplate(formTemplateId)) {
      if (app.status === 'draft') continue;
      const area = app.sectionAnswers?.PROJECT_INFORMATION?.oecdArea ?? 'Sin clasificar';
      counts[area] = (counts[area] ?? 0) + 1;
    }
    return delay(
      Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([area, count]) => ({ area, count }))
    );
  }

  async getAdmissibilityFunnel(formTemplateId?: string | null): Promise<{
    drafts: number;
    submitted: number;
    pending: number;
    underReview: number;
    admissible: number;
    notAdmissible: number;
  }> {
    const apps = this.appsForTemplate(formTemplateId);
    const inFunnel = ['submitted', 'under_admissibility_review', 'admissible', 'not_admissible',
      'under_evaluation', 'evaluated', 'selected', 'not_selected'];
    // Exclusive counts — pending + underReview + admissible + notAdmissible === submitted
    return delay({
      drafts: apps.filter((a) => a.status === 'draft').length,
      submitted: apps.filter((a) => inFunnel.includes(a.status)).length,
      pending: apps.filter((a) => a.status === 'submitted').length,
      underReview: apps.filter((a) => a.status === 'under_admissibility_review').length,
      admissible: apps.filter((a) =>
        ['admissible', 'under_evaluation', 'evaluated', 'selected', 'not_selected'].includes(a.status)
      ).length,
      notAdmissible: apps.filter((a) => a.status === 'not_admissible').length,
    });
  }

  async getEvaluationFunnel(formTemplateId?: string | null): Promise<{
    admissible: number;
    pendingEval: number;
    underEvaluation: number;
    evaluated: number;
    awarded: number;
    notAwarded: number;
  }> {
    const apps = this.appsForTemplate(formTemplateId);
    const eligible = apps.filter((a) =>
      ['admissible', 'under_evaluation', 'evaluated', 'selected', 'not_selected'].includes(a.status)
    );
    const threshold = ALERT_CONFIG.SCORE_VARIANCE_THRESHOLD;

    let pendingEval = 0;
    let underEvaluation = 0;
    let evaluated = 0;

    for (const app of eligible) {
      if (app.status === 'admissible') {
        pendingEval++;
      } else if (app.status === 'under_evaluation') {
        underEvaluation++;
      } else {
        // evaluated / selected / not_selected — check for unresolved arbitration
        const assignments = db.assignments.filter((a) => a.applicationId === app.id);
        const active = assignments
          .filter((a) => a.status !== 'declined' && a.status !== 'conflict_declared')
          .sort((a, b) => new Date(a.assignedAt).getTime() - new Date(b.assignedAt).getTime());
        const arbiterIds = new Set(active.length > 2 ? active.slice(2).map((a) => a.id) : []);
        const hasArbiter = arbiterIds.size > 0;
        const arbiterDone = active
          .filter((a) => arbiterIds.has(a.id))
          .every((a) => a.status === 'completed');

        const completedScores = active
          .filter((a) => !arbiterIds.has(a.id) && a.status === 'completed')
          .map((a) => {
            const ev = db.evaluations.find((e) => e.assignmentId === a.id);
            return ev?.submittedAt ? weightedScore(ev.criteriaEvaluations) : null;
          })
          .filter((s): s is number => s !== null);

        const maxDev =
          completedScores.length >= 2
            ? Math.max(...completedScores) - Math.min(...completedScores)
            : null;
        const hasDeviation = maxDev !== null && maxDev > threshold;
        const arbitrationPending =
          (hasDeviation && !hasArbiter) || (hasDeviation && hasArbiter && !arbiterDone);

        if (arbitrationPending) {
          underEvaluation++;
        } else {
          evaluated++;
        }
      }
    }

    return delay({
      admissible: eligible.length,
      pendingEval,
      underEvaluation,
      evaluated,
      awarded: apps.filter((a) => a.status === 'selected').length,
      notAwarded: apps.filter((a) => a.status === 'not_selected').length,
    });
  }

  async getPendingByEvaluator(): Promise<Array<{ evaluatorName: string; pending: number }>> {
    const evaluators = db.users.filter((u) => u.role === 'evaluator');
    return delay(
      evaluators
        .map((ev) => ({
          evaluatorName: ev.name,
          pending: db.assignments.filter(
            (a) => a.evaluatorId === ev.id &&
              (a.status === 'pending' || a.status === 'accepted_not_started' || a.status === 'in_progress')
          ).length,
        }))
        .filter((r) => r.pending > 0)
        .sort((a, b) => b.pending - a.pending)
    );
  }

  async getArbitrationApplications(formTemplateId?: string | null): Promise<
    Array<{ applicationNumber: string; title: string; activeEvaluators: number }>
  > {
    return delay(
      this.appsForTemplate(formTemplateId)
        .map((app) => {
          const active = db.assignments.filter(
            (a) => a.applicationId === app.id && a.status !== 'conflict_declared'
          ).length;
          return { app, active };
        })
        .filter(({ active }) => active >= 3)
        .map(({ app, active }) => ({
          applicationNumber: app.applicationNumber,
          title: app.sectionAnswers?.PROJECT_INFORMATION?.projectTitle ?? app.applicationNumber,
          activeEvaluators: active,
        }))
    );
  }

  async getDeadlineAlerts(daysAhead: number = 14, formTemplateId?: string | null): Promise<
    Array<{ name: string; deadline: string; daysUntil: number; isOverdue: boolean }>
  > {
    const now = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;
    const GRACE_DAYS = 3;
    const templates = formTemplateId
      ? db.formTemplates.filter((t) => t.id === formTemplateId)
      : db.formTemplates;
    return delay(
      templates
        .filter((t) => {
          if (!t.deadline) return false;
          const d = new Date(t.deadline).getTime();
          const days = (d - now) / DAY_MS;
          return days <= daysAhead && days >= -GRACE_DAYS;
        })
        .map((t) => {
          const daysUntil = Math.round((new Date(t.deadline).getTime() - now) / DAY_MS);
          return { name: t.name, deadline: t.deadline, daysUntil, isOverdue: daysUntil < 0 };
        })
        .sort((a, b) => a.daysUntil - b.daysUntil)
    );
  }

  // ─── Report methods (REP-010 to REP-013) ──────────────────────────────────

  // REP-010: total assigned vs completed vs rejected per evaluator
  async getEvaluatorAssignmentReport(formTemplateId?: string | null): Promise<
    Array<{
      evaluatorId: string;
      evaluatorName: string;
      email: string;
      isExternal: boolean;
      pending: number;
      completed: number;
      rejected: number;
      total: number;
    }>
  > {
    const appIds = this.appIdsForTemplate(formTemplateId);
    const evaluators = db.users.filter((u) => u.role === 'evaluator');
    const result = evaluators
      .map((ev) => {
        const asgns = db.assignments.filter(
          (a) => a.evaluatorId === ev.id && (!formTemplateId || appIds.has(a.applicationId))
        );
        if (asgns.length === 0) return null;
        return {
          evaluatorId: ev.id,
          evaluatorName: ev.name,
          email: ev.email,
          isExternal: (ev as any).isExternal ?? false,
          pending: asgns.filter((a) => a.status === 'pending' || a.status === 'accepted_not_started').length,
          completed: asgns.filter((a) => a.status === 'completed').length,
          rejected: asgns.filter((a) => a.status === 'conflict_declared').length,
          total: asgns.length,
        };
      })
      .filter((r): r is NonNullable<typeof r> => r !== null)
      .sort((a, b) => b.total - a.total);
    return delay(result);
  }

  async exportEvaluatorAssignmentReport(
    rows: Array<{ evaluatorName: string; email: string; isExternal: boolean; pending: number; completed: number; rejected: number; total: number }>
  ): Promise<void> {
    const header = 'evaluador,email,externo,pendientes,completadas,rechazadas,total\n';
    const body = rows
      .map((r) =>
        [r.evaluatorName, r.email, r.isExternal ? 'Sí' : 'No', r.pending, r.completed, r.rejected, r.total].join(',')
      )
      .join('\n');
    triggerDownload(header + body, 'rep010-carga-evaluadores.csv', 'text/csv');
  }

  // REP-011: rejected assignments (conflict declared or explicitly declined)
  async getRejectedAssignments(formTemplateId?: string | null): Promise<
    Array<{
      assignmentId: string;
      evaluatorName: string;
      email: string;
      applicationNumber: string;
      reason: string | null;
      declineReason: 'thematic' | 'conflict' | 'other' | null;
      declineNote: string | null;
      conflictDeclaredAt: string | null;
    }>
  > {
    const DECLINE_LABELS: Record<string, string> = {
      thematic: 'Área no afín',
      conflict: 'Conflicto de interés',
      other: 'Otro motivo',
    };
    const appIds = this.appIdsForTemplate(formTemplateId);
    const rows = db.assignments
      .filter(
        (a) =>
          (a.status === 'conflict_declared' || a.status === 'declined') &&
          (!formTemplateId || appIds.has(a.applicationId))
      )
      .map((a) => {
        const ev = findUser(a.evaluatorId);
        const app = findApp(a.applicationId);
        const dr = (a as any).declineReason as string | undefined;
        return {
          assignmentId: a.id,
          evaluatorName: ev?.name ?? 'Evaluador',
          email: ev?.email ?? '',
          applicationNumber: app?.applicationNumber ?? a.applicationId,
          reason: a.status === 'declined'
            ? (dr ? DECLINE_LABELS[dr] ?? 'Invitación rechazada' : 'Invitación rechazada')
            : (a.conflictDetails ?? 'Conflicto de interés declarado'),
          declineReason: dr as 'thematic' | 'conflict' | 'other' | null ?? null,
          declineNote: (a as any).declineNote ?? null,
          conflictDeclaredAt: a.conflictDeclaredAt,
        };
      })
      .sort((a, b) =>
        (b.conflictDeclaredAt ?? '').localeCompare(a.conflictDeclaredAt ?? '')
      );
    return delay(rows);
  }

  /**
   * Decline/conflict history per evaluator, for the assignment modal: lets
   * the admin see why a candidate turned down past invitations before
   * inviting them again.
   */
  async getEvaluatorDeclineHistories(): Promise<
    Record<
      string,
      Array<{
        applicationNumber: string;
        kind: 'declined' | 'conflict_declared';
        reasonLabel: string;
        note: string | null;
      }>
    >
  > {
    const DECLINE_LABELS: Record<string, string> = {
      thematic: 'Área no afín',
      conflict: 'Conflicto de interés',
      other: 'Otro motivo',
    };
    const map: Record<
      string,
      Array<{
        applicationNumber: string;
        kind: 'declined' | 'conflict_declared';
        reasonLabel: string;
        note: string | null;
      }>
    > = {};
    db.assignments
      .filter(
        (a) => a.status === 'declined' || a.status === 'conflict_declared'
      )
      .forEach((a) => {
        const app = findApp(a.applicationId);
        const entry =
          a.status === 'declined'
            ? {
                applicationNumber: app?.applicationNumber ?? a.applicationId,
                kind: 'declined' as const,
                reasonLabel:
                  DECLINE_LABELS[(a as any).declineReason as string] ??
                  'Otro motivo',
                note: ((a as any).declineNote as string | null) ?? null,
              }
            : {
                applicationNumber: app?.applicationNumber ?? a.applicationId,
                kind: 'conflict_declared' as const,
                reasonLabel: 'Conflicto de interés',
                note: a.conflictDetails ?? null,
              };
        if (!map[a.evaluatorId]) map[a.evaluatorId] = [];
        map[a.evaluatorId].push(entry);
      });
    return delay(map);
  }

  async exportRejectedAssignments(
    rows: Array<{ evaluatorName: string; email: string; applicationNumber: string; reason: string | null; declineNote?: string | null; conflictDeclaredAt: string | null }>
  ): Promise<void> {
    const header = 'evaluador,email,postulacion,motivo,justificacion,fecha_declaracion\n';
    const body = rows
      .map((r) =>
        [
          r.evaluatorName,
          r.email,
          r.applicationNumber,
          `"${(r.reason ?? '').replace(/"/g, '""')}"`,
          `"${(r.declineNote ?? '').replace(/"/g, '""')}"`,
          r.conflictDeclaredAt ? new Date(r.conflictDeclaredAt).toLocaleDateString('es-CL') : '',
        ].join(',')
      )
      .join('\n');
    triggerDownload(header + body, 'rep011-asignaciones-rechazadas.csv', 'text/csv');
  }

  // REP-010 complement: pool stats for assignment redistribution decisions
  async getAssignmentPoolStats(formTemplateId?: string | null): Promise<{
    total: number;
    assigned: number;
    unassigned: number;
  }> {
    const evaluable = this.appsForTemplate(formTemplateId).filter((a) => a.status !== 'draft');
    const assignedIds = new Set(db.assignments.map((a) => a.applicationId));
    const unassigned = evaluable.filter((a) => !assignedIds.has(a.id)).length;
    return delay({ total: evaluable.length, assigned: evaluable.length - unassigned, unassigned });
  }

  // REP-012: evaluations overdue (assigned, not completed, past threshold days)
  async getOverdueEvaluations(thresholdDays: number = 7, formTemplateId?: string | null): Promise<
    Array<{
      assignmentId: string;
      evaluatorName: string;
      email: string;
      applicationNumber: string;
      applicationTitle: string;
      assignedAt: string;
      daysOverdue: number;
    }>
  > {
    const now = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;
    const appIds = this.appIdsForTemplate(formTemplateId);
    const rows = db.assignments
      .filter(
        (a) =>
          (a.status === 'pending' || a.status === 'accepted_not_started') &&
          (!formTemplateId || appIds.has(a.applicationId))
      )
      .map((a) => {
        const daysSince = (now - new Date(a.assignedAt).getTime()) / DAY_MS;
        if (daysSince < thresholdDays) return null;
        const ev = findUser(a.evaluatorId);
        const app = findApp(a.applicationId);
        return {
          assignmentId: a.id,
          evaluatorName: ev?.name ?? 'Evaluador',
          email: ev?.email ?? '',
          applicationNumber: app?.applicationNumber ?? a.applicationId,
          applicationTitle: app?.sectionAnswers?.PROJECT_INFORMATION?.projectTitle ?? app?.applicationNumber ?? '',
          assignedAt: a.assignedAt,
          daysOverdue: Math.floor(daysSince),
        };
      })
      .filter((r): r is NonNullable<typeof r> => r !== null)
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
    return delay(rows);
  }

  async exportOverdueEvaluations(
    rows: Array<{ evaluatorName: string; email: string; applicationNumber: string; applicationTitle: string; assignedAt: string; daysOverdue: number }>
  ): Promise<void> {
    const header = 'evaluador,email,postulacion,titulo_proyecto,fecha_asignacion,dias_atraso\n';
    const body = rows
      .map((r) =>
        [
          r.evaluatorName,
          r.email,
          r.applicationNumber,
          `"${(r.applicationTitle ?? '').replace(/"/g, '""')}"`,
          new Date(r.assignedAt).toLocaleDateString('es-CL'),
          r.daysOverdue,
        ].join(',')
      )
      .join('\n');
    triggerDownload(header + body, 'rep012-evaluaciones-atrasadas.csv', 'text/csv');
  }

  // REP-013: evaluators with no login since assignment (or never logged in)
  async getInactiveEvaluatorReport(formTemplateId?: string | null): Promise<
    Array<{
      evaluatorId: string;
      evaluatorName: string;
      email: string;
      isExternal: boolean;
      lastLoginAt: string | null;
      applicationNumber: string;
      assignedAt: string;
      daysSinceAssigned: number;
    }>
  > {
    const now = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;
    const appIds = this.appIdsForTemplate(formTemplateId);
    const rows: Array<{
      evaluatorId: string;
      evaluatorName: string;
      email: string;
      isExternal: boolean;
      lastLoginAt: string | null;
      applicationNumber: string;
      assignedAt: string;
      daysSinceAssigned: number;
    }> = [];

    for (const asg of db.assignments) {
      if (asg.status !== 'pending' && asg.status !== 'accepted_not_started' && asg.status !== 'in_progress') continue;
      if (formTemplateId && !appIds.has(asg.applicationId)) continue;
      const ev = findUser(asg.evaluatorId);
      if (!ev) continue;
      const lastLogin = (ev as any).lastLoginAt ?? null;
      const neverLogged = !lastLogin;
      const loggedBeforeAssigned = lastLogin && new Date(lastLogin) < new Date(asg.assignedAt);
      if (!neverLogged && !loggedBeforeAssigned) continue;
      const app = findApp(asg.applicationId);
      rows.push({
        evaluatorId: ev.id,
        evaluatorName: ev.name,
        email: ev.email,
        isExternal: (ev as any).isExternal ?? false,
        lastLoginAt: lastLogin,
        applicationNumber: app?.applicationNumber ?? asg.applicationId,
        assignedAt: asg.assignedAt,
        daysSinceAssigned: Math.floor((now - new Date(asg.assignedAt).getTime()) / DAY_MS),
      });
    }
    rows.sort((a, b) => b.daysSinceAssigned - a.daysSinceAssigned);
    return delay(rows);
  }

  async exportInactiveEvaluatorReport(
    rows: Array<{ evaluatorName: string; email: string; isExternal: boolean; lastLoginAt: string | null; applicationNumber: string; assignedAt: string; daysSinceAssigned: number }>
  ): Promise<void> {
    const header = 'evaluador,email,externo,ultimo_acceso,postulacion,fecha_asignacion,dias_sin_actividad\n';
    const body = rows
      .map((r) =>
        [
          r.evaluatorName,
          r.email,
          r.isExternal ? 'Sí' : 'No',
          r.lastLoginAt ? new Date(r.lastLoginAt).toLocaleDateString('es-CL') : 'Nunca',
          r.applicationNumber,
          new Date(r.assignedAt).toLocaleDateString('es-CL'),
          r.daysSinceAssigned,
        ].join(',')
      )
      .join('\n');
    triggerDownload(header + body, 'rep013-evaluadores-inactivos.csv', 'text/csv');
  }

  // ─── Evaluador dashboard (Evaluadores tab) ─────────────────────────────────

  async getEvaluatorDashboard(formTemplateId?: string | null): Promise<
    Array<{
      id: string;
      name: string;
      email: string;
      isExternal: boolean;
      isConfirmed: boolean;
      lastLoginAt: string | null;
      pending: number;
      completed: number;
      conflictDeclared: number;
      declined: number;
      total: number;
      mostOverdueDays: number;
    }>
  > {
    const now = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;
    const THRESHOLD = ALERT_CONFIG.INVITATION_RESPONSE_DAYS;
    const appIds = this.appIdsForTemplate(formTemplateId);
    const evaluators = db.users.filter((u) => u.role === 'evaluator');
    const result = evaluators.map((ev) => {
      const asgns = db.assignments.filter(
        (a) => a.evaluatorId === ev.id && (!formTemplateId || appIds.has(a.applicationId))
      );
      const pending = asgns.filter((a) => a.status === 'pending' || a.status === 'accepted_not_started' || a.status === 'in_progress');
      const overdueDays = pending
        .map((a) => Math.floor((now - new Date(a.assignedAt).getTime()) / DAY_MS))
        .filter((d) => d >= THRESHOLD);
      return {
        id: ev.id,
        name: ev.name,
        email: ev.email,
        isExternal: (ev as any).isExternal ?? false,
        isConfirmed: (ev as any).isConfirmed ?? false,
        lastLoginAt: (ev as any).lastLoginAt ?? null,
        pending: pending.length,
        completed: asgns.filter((a) => a.status === 'completed').length,
        conflictDeclared: asgns.filter((a) => a.status === 'conflict_declared').length,
        declined: asgns.filter((a) => a.status === 'declined').length,
        total: asgns.length,
        mostOverdueDays: overdueDays.length > 0 ? Math.max(...overdueDays) : 0,
      };
    });
    result.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'es'));
    return delay(result);
  }

  // ─── Project composition report (REP-COMP) ─────────────────────────────────
  //
  // BACKEND NOTE (REP-COMP):
  // - "Tipo Consolidado/Emergente" derives from principalInvestigator.type.
  // - "Liderado por UC" means the IP has a facultadUC field (is a UC academic).
  //   Backend should cross-check against the UC staff registry; do not accept
  //   this as a self-declared flag.
  // - "COI interno UC" = coInvestigator.facultadUC is set.
  //   "COI externo" = coInvestigator.facultadUC is null/undefined.
  async getProjectCompositionReport(formTemplateId?: string | null): Promise<{
    total: number;
    consolidated: number;
    emerging: number;
    totalPeople: number;
    irCount: number;
    ipCount: number;
    iaCount: number;
    coiCount: number;
    menTotal: number;
    womenTotal: number;
    irMen: number;
    irWomen: number;
    irConsolidated: number;
    irEmerging: number;
    ipConsolidated: number;
    ipEmerging: number;
    uniquePeople: number;
    uniqueMen: number;
    uniqueWomen: number;
    // DEV NOTE: ucCombinations is dynamic — keys depend on Contest.config (max UC members, allowed roles).
    // Production should derive combinations from Contest.config rather than hardcoding the 10 possible combos.
    ucCombinations: Array<{ label: string; ipCount: number; iaCount: number; projects: number }>;
    coiNone: number;
    coiNationalOnly: number;
    coiInternationalOnly: number;
    coiBoth: number;
  }> {
    await delay(80);
    const apps = this.appsForTemplate(formTemplateId).filter((a) => a.status !== 'draft');
    let consolidated = 0, emerging = 0;
    let totalPeople = 0, irCount = 0, ipCount = 0, iaCount = 0, coiCount = 0;
    let menTotal = 0, womenTotal = 0, irMen = 0, irWomen = 0;
    let irConsolidated = 0, irEmerging = 0, ipConsolidated = 0, ipEmerging = 0;
    let coiNone = 0, coiNationalOnly = 0, coiInternationalOnly = 0, coiBoth = 0;
    const ucComboMap: Record<string, { ipCount: number; iaCount: number; projects: number }> = {};
    const uniquePersonMap = new Map<string, { gender: string }>();

    for (const app of apps) {
      const team = app.sectionAnswers?.PROJECT_TEAM;
      if (!team) continue;
      const ir: any = team.principalInvestigator;
      const coInvs: any[] = Array.isArray(team.coInvestigators) ? team.coInvestigators : [];
      const ips = coInvs.filter((c: any) => c.role === 'IP');
      const ias = coInvs.filter((c: any) => c.role === 'IA');
      const cois = coInvs.filter((c: any) => c.role === 'COI');

      // DEV NOTE: PROTOTYPE VALUE — production: Contest.config.classificationThreshold (Date, mes incluido)
      // See src/utils/classificationUtils.ts for threshold definition.
      // consolidated/emerging: IR and IP count; IA excluded
      const irIsConsolidated = isConsolidated(ir?.doctorateYear, ir?.doctorateMonth);
      const anyIPConsolidated = ips.some((p: any) => isConsolidated(p.doctorateYear, p.doctorateMonth));
      if (irIsConsolidated || anyIPConsolidated) consolidated++; else emerging++;

      // role counts
      irCount++;
      ipCount += ips.length;
      iaCount += ias.length;
      coiCount += cois.length;

      // IR researcher profile (Consolidado / Emergente)
      if (irIsConsolidated) irConsolidated++; else irEmerging++;

      // IP researcher profiles
      for (const ip of ips) {
        if (isConsolidated(ip.doctorateYear, ip.doctorateMonth)) ipConsolidated++; else ipEmerging++;
      }

      // people & gender (role participations — one person in N projects counts N times)
      const allMembers = [ir, ...coInvs].filter(Boolean);
      totalPeople += allMembers.length;
      for (const m of allMembers) {
        if (m.gender === 'M') menTotal++; else if (m.gender === 'F') womenTotal++;
        // unique people: dedup by run (for UC) or email (for COI)
        const uKey = ((m.run?.trim() || m.email?.trim()) || '').toLowerCase();
        if (uKey && !uniquePersonMap.has(uKey)) {
          uniquePersonMap.set(uKey, { gender: m.gender });
        }
      }
      if (ir?.gender === 'M') irMen++; else if (ir?.gender === 'F') irWomen++;

      // UC team combinations (IR always present; combinations are IP + IA counts)
      const comboKey = `${ips.length}_${ias.length}`;
      if (!ucComboMap[comboKey]) ucComboMap[comboKey] = { ipCount: ips.length, iaCount: ias.length, projects: 0 };
      ucComboMap[comboKey].projects++;

      // COI presence by collaboration type
      const hasNational = cois.some((c: any) => c.institutionType === 'Nacional');
      const hasInternational = cois.some((c: any) => c.institutionType === 'Internacional');
      if (cois.length === 0) coiNone++;
      else if (hasNational && hasInternational) coiBoth++;
      else if (hasInternational) coiInternationalOnly++;
      else coiNationalOnly++;
    }

    const ucCombinations = Object.values(ucComboMap)
      .sort((a, b) => {
        const totalA = a.ipCount + a.iaCount;
        const totalB = b.ipCount + b.iaCount;
        if (totalA !== totalB) return totalA - totalB;
        return b.ipCount - a.ipCount; // more IP first within same UC count
      })
      .map((c) => {
        const parts: string[] = ['1 IR'];
        if (c.ipCount === 1) parts.push('1 IP');
        else if (c.ipCount > 1) parts.push(`${c.ipCount} IP`);
        if (c.iaCount === 1) parts.push('1 IA');
        else if (c.iaCount > 1) parts.push(`${c.iaCount} IA`);
        return { ...c, label: parts.join(' + ') };
      });

    const uniquePeople = uniquePersonMap.size;
    const uniqueMen = Array.from(uniquePersonMap.values()).filter((p) => p.gender === 'M').length;
    const uniqueWomen = Array.from(uniquePersonMap.values()).filter((p) => p.gender === 'F').length;

    return {
      total: apps.length, consolidated, emerging,
      totalPeople, irCount, ipCount, iaCount, coiCount,
      menTotal, womenTotal, irMen, irWomen,
      irConsolidated, irEmerging, ipConsolidated, ipEmerging,
      uniquePeople, uniqueMen, uniqueWomen,
      ucCombinations, coiNone, coiNationalOnly, coiInternationalOnly, coiBoth,
    };
  }

  exportProjectCompositionReport(data: {
    total: number; consolidated: number; emerging: number;
    totalPeople: number; irCount: number; ipCount: number; iaCount: number; coiCount: number;
    menTotal: number; womenTotal: number; irMen: number; irWomen: number;
    irConsolidated: number; irEmerging: number; ipConsolidated: number; ipEmerging: number;
    uniquePeople: number; uniqueMen: number; uniqueWomen: number;
    ucCombinations: Array<{ label: string; projects: number }>;
    coiNone: number; coiNationalOnly: number; coiInternationalOnly: number; coiBoth: number;
  }): void {
    const rows: [string, number][] = [
      ['Proyectos enviados (total)', data.total],
      ['Cat. A (Consolidado)', data.consolidated],
      ['Cat. B (Emergente)', data.emerging],
      ['-- Participaciones en equipo --', 0],
      ['Total participaciones', data.totalPeople],
      ['IR', data.irCount],
      ['IP', data.ipCount],
      ['IA', data.iaCount],
      ['COI', data.coiCount],
      ['Hombres (participaciones)', data.menTotal],
      ['Mujeres (participaciones)', data.womenTotal],
      ['-- Perfil IR e IP --', 0],
      ['IR consolidados', data.irConsolidated],
      ['IR emergentes', data.irEmerging],
      ['IP consolidados', data.ipConsolidated],
      ['IP emergentes', data.ipEmerging],
      ['-- Personas únicas --', 0],
      ['Personas únicas (total)', data.uniquePeople],
      ['Hombres únicos', data.uniqueMen],
      ['Mujeres únicas', data.uniqueWomen],
      ...data.ucCombinations.map((r): [string, number] => [`Estructura UC: ${r.label}`, r.projects]),
      ['COI: Sin coinvestigadores', data.coiNone],
      ['COI: Solo nacional', data.coiNationalOnly],
      ['COI: Solo internacional', data.coiInternationalOnly],
      ['COI: Nacional e internacional', data.coiBoth],
    ];
    const body = rows.map(([l, v]) => `${l},${v}`).join('\n');
    triggerDownload('metrica,valor\n' + body, 'rep-comp-composicion.csv', 'text/csv');
  }

  // ─── Institution report (REP-INST) ────────────────────────────────────────
  //
  // BACKEND NOTE (REP-INST): This report requires the `country` field on Researcher
  // to be populated for every team member (IP and all COIs). In the prototype it is
  // optional; the backend MUST enforce it as a required field.
  // The `institutionType` flag ('Nacional'|'Internacional') must be derived from
  // `country === 'Chile'` — do not let it be stored independently, to avoid drift.
  //
  // Counting method: each unique (affiliation, country) pair is one institution.
  // The same string affiliation appearing in two apps counts as one institution.
  async getInstitutionReport(formTemplateId?: string | null): Promise<{
    totalUnique: number;
    national: number;
    international: number;
    withInternationalCollab: number;
    byCountry: Array<{ country: string; total: number; institutions: Array<{ name: string; count: number }> }>;
  }> {
    await delay(80);
    const apps = this.appsForTemplate(formTemplateId).filter((a) => a.status !== 'draft');
    // affiliation → { country, appIds: Set<string> }
    const instMap = new Map<string, { country: string; appIds: Set<string> }>();
    for (const app of apps) {
      const team = app.sectionAnswers?.PROJECT_TEAM;
      if (!team) continue;
      const members: Array<{ affiliation?: string; country?: string }> = [];
      if (team.principalInvestigator) members.push(team.principalInvestigator);
      if (Array.isArray(team.coInvestigators)) members.push(...team.coInvestigators);
      for (const m of members) {
        const aff = (m.affiliation ?? '').trim();
        if (!aff) continue;
        if (!instMap.has(aff)) {
          instMap.set(aff, { country: m.country ?? 'Chile', appIds: new Set() });
        }
        instMap.get(aff)!.appIds.add(app.id);
      }
    }
    // Group by country
    const countryMap = new Map<string, Array<{ name: string; count: number }>>();
    Array.from(instMap.entries()).forEach(([aff, { country, appIds }]) => {
      if (!countryMap.has(country)) countryMap.set(country, []);
      countryMap.get(country)!.push({ name: aff, count: appIds.size });
    });
    const byCountry = Array.from(countryMap.entries())
      .map(([country, institutions]) => ({
        country,
        total: institutions.length,
        institutions: institutions.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'es')),
      }))
      .sort((a, b) => b.total - a.total || a.country.localeCompare(b.country, 'es'));
    const national = countryMap.get('Chile')?.length ?? 0;
    const international = Array.from(countryMap.entries())
      .filter(([c]) => c !== 'Chile')
      .reduce((s, [, insts]) => s + insts.length, 0);
    const withInternationalCollab = apps.filter((app) => {
      const team = app.sectionAnswers?.PROJECT_TEAM;
      if (!team) return false;
      const members: Array<{ country?: string }> = [
        team.principalInvestigator,
        ...(Array.isArray(team.coInvestigators) ? team.coInvestigators : []),
      ].filter(Boolean);
      return members.some((m) => m.country && m.country !== 'Chile');
    }).length;
    return { totalUnique: instMap.size, national, international, withInternationalCollab, byCountry };
  }

  exportInstitutionReport(byCountry: Array<{ country: string; total: number; institutions: Array<{ name: string; count: number }> }>): void {
    const header = 'pais,institucion,postulaciones\n';
    const body = byCountry
      .flatMap((c) => c.institutions.map((i) => `"${c.country}","${i.name}",${i.count}`))
      .join('\n');
    triggerDownload(header + body, 'rep-inst-instituciones.csv', 'text/csv');
  }

  async notifySelectedAndNotSelected(payload: {
    applicationIds: string[];
  }): Promise<{
    notifiedAt: string;
    selectedCount: number;
    notSelectedCount: number;
    adminName: string;
  }> {
    const ts = new Date().toISOString();
    let selectedCount = 0;
    let notSelectedCount = 0;
    payload.applicationIds.forEach((id) => {
      const app = findApp(id);
      if (!app) return;
      app.awardNotifiedAt = ts;
      if (app.status === 'selected') selectedCount++;
      if (app.status === 'not_selected') notSelectedCount++;
    });
    persist();
    return delay({
      notifiedAt: ts,
      selectedCount,
      notSelectedCount,
      adminName: 'Admin DINV',
    });
  }

  async getNotificationStats(formTemplateId?: string | null): Promise<{
    selected: Array<{ id: string; applicationNumber: string; projectTitle: string; user: string }>;
    not_selected: Array<{ id: string; applicationNumber: string; projectTitle: string; user: string }>;
    selected_notified: Array<{ id: string; applicationNumber: string; projectTitle: string; user: string }>;
    not_selected_notified: Array<{ id: string; applicationNumber: string; projectTitle: string; user: string }>;
  }> {
    const toItem = (a: ReturnType<typeof findApp> & {}) => ({
      id: a.id,
      applicationNumber: a.applicationNumber,
      projectTitle: a.sectionAnswers?.PROJECT_INFORMATION?.projectTitle ?? a.applicationNumber,
      user: findUser(a.userId)?.name ?? 'Usuario',
    });

    const scoped = this.appsForTemplate(formTemplateId);
    const selected = scoped.filter((a) => a.status === 'selected' && !a.awardNotifiedAt);
    const notSelected = scoped.filter(
      (a) => a.status === 'not_selected' && !a.awardNotifiedAt
    );
    const selectedNotified = scoped.filter(
      (a) => a.status === 'selected' && a.awardNotifiedAt
    );
    const notSelectedNotified = scoped.filter(
      (a) => a.status === 'not_selected' && a.awardNotifiedAt
    );

    return delay({
      selected: selected.map(toItem),
      not_selected: notSelected.map(toItem),
      selected_notified: selectedNotified.map(toItem),
      not_selected_notified: notSelectedNotified.map(toItem),
    });
  }

  async getEvaluationsBoardData(formTemplateId?: string | null): Promise<EvalBoardRow[]> {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const { EVALUATION_DELAYED_MEDIUM_DAYS } = ALERT_CONFIG;

    const apps = this.appsForTemplate(formTemplateId).filter(
      (a) => ['admissible', 'under_evaluation', 'evaluated'].includes(a.status)
    );

    const rows: EvalBoardRow[] = apps.map((app) => {
      const appAssignments = db.assignments.filter((a) => a.applicationId === app.id);
      const nonRejectedAssignments = appAssignments.filter(
        (a) => a.status !== 'declined' && a.status !== 'conflict_declared'
      );
      const acceptedAssignments = appAssignments.filter(
        (a) => a.status === 'accepted_not_started' || a.status === 'in_progress' || a.status === 'completed'
      );
      // Keep activeAssignments as alias for downstream progress/arbitration logic
      const activeAssignments = nonRejectedAssignments;

      // Compute per-assignment detail
      const assignmentDetails: EvalBoardAssignment[] = appAssignments.map((a) => {
        const evaluator = findUser(a.evaluatorId);
        const evaluation = db.evaluations.find(
          (e) => e.assignmentId === a.id && e.submittedAt
        );
        const finalScore = evaluation
          ? calcFinalScore(evaluation.criteriaEvaluations)
          : null;
        return {
          evaluatorId: a.evaluatorId,
          evaluatorName: evaluator?.name ?? 'Evaluador desconocido',
          status: a.status,
          daysSinceAssigned: (now - new Date(a.assignedAt).getTime()) / DAY_MS,
          finalScore,
          conflictDetails: a.conflictDetails ?? null,
          declineReason: (a as any).declineReason ?? null,
          declineNote: (a as any).declineNote ?? null,
        };
      });

      // Coverage — based on accepted (not just non-rejected) evaluators.
      // 'assigned' se separa de 'invited': hay evaluadores puestos, pero ninguna
      // invitación salió todavía, así que decir "Con invitación" sería falso.
      const acceptedCount = acceptedAssignments.length;
      const activeCount = nonRejectedAssignments.length;
      const anyInvited = nonRejectedAssignments.some((a) => !isNotInvited(a));
      const coverage =
        acceptedCount >= 2 ? ('complete' as const)
        : acceptedCount >= 1 ? ('incomplete' as const)
        : nonRejectedAssignments.length === 0 ? ('none' as const)
        : anyInvited ? ('invited' as const)
        : ('assigned' as const);

      // Progress
      const completedActive = activeAssignments.filter((a) => a.status === 'completed');
      const inFlightActive = activeAssignments.filter(
        (a) => a.status === 'accepted_not_started' || a.status === 'in_progress'
      );
      const isOverdue = inFlightActive.some(
        (a) => (now - new Date(a.assignedAt).getTime()) / DAY_MS >= EVALUATION_DELAYED_MEDIUM_DAYS
      );
      const daysOverdue = isOverdue
        ? Math.max(
            ...inFlightActive
              .map((a) => Math.floor((now - new Date(a.assignedAt).getTime()) / DAY_MS) - EVALUATION_DELAYED_MEDIUM_DAYS)
              .filter((d) => d >= 0)
          )
        : 0;

      // Identify the arbiter (most recently assigned active evaluator beyond the first 2)
      const hasArbiter = activeCount >= 3;
      const arbiter = hasArbiter
        ? [...activeAssignments].sort(
            (a, b) => new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime()
          )[0]
        : null;

      // Primaries (all but the arbiter) have all completed; arbiter still active
      const onlyArbiterActive =
        hasArbiter &&
        arbiter !== null &&
        arbiter.status !== 'completed' &&
        activeAssignments.filter((a) => a.id !== arbiter.id).every((a) => a.status === 'completed');

      const allCompleted = activeCount > 0 && completedActive.length >= activeCount;

      // Scores — computed before progress so deviation can inform the progress label
      const completedScores = assignmentDetails
        .filter((a) => a.finalScore !== null)
        .map((a) => a.finalScore as number);
      const avgScore =
        completedScores.length > 0
          ? completedScores.reduce((s, v) => s + v, 0) / completedScores.length
          : null;
      const maxDeviation =
        completedScores.length >= 2
          ? Math.max(...completedScores) - Math.min(...completedScores)
          : null;
      const threshold = ALERT_CONFIG.SCORE_VARIANCE_THRESHOLD;
      const hasDeviation = maxDeviation !== null && maxDeviation > threshold;
      // Primaries done with deviation but no arbiter assigned yet → still needs work
      const requiresArbiter = hasDeviation && !hasArbiter;

      const progress: ProgressLabel =
        allCompleted && !requiresArbiter ? 'received'
        : allCompleted && requiresArbiter ? 'in_arbitration'
        : onlyArbiterActive ? 'in_arbitration'
        : isOverdue ? 'overdue'
        : inFlightActive.length > 0 ? 'in_progress'
        : 'not_started';

      // Result
      const result = ((): ResultLabel => {
        if (!hasDeviation && progress === 'received') return 'resolved';
        if (!hasDeviation) return 'no_deviation';
        if (activeCount < 3) return 'requires_arbitration';
        if (!arbiter) return 'requires_arbitration';
        if (arbiter.status === 'completed') return 'arbitration_resolved';
        if (arbiter.status === 'pending') return 'arbiter_invited';
        if (arbiter.status === 'accepted_not_started') return 'arbiter_accepted';
        return 'arbitration_in_progress';
      })();

      // IR name + UA
      const irUser = findUser(app.userId);
      const irName = irUser?.name ?? '—';
      const unidadAcademica =
        irUser?.userProfile?.unidadAcademica ?? '—';
      const title =
        (app as any).sectionAnswers?.PROJECT_INFORMATION?.projectTitle ?? app.applicationNumber;

      return {
        applicationId: app.id,
        applicationNumber: app.applicationNumber,
        title,
        irName,
        unidadAcademica,
        coverage,
        progress,
        daysOverdue,
        assignments: assignmentDetails,
        completedScores,
        avgScore,
        maxDeviation,
        result,
      };
    });

    return delay(rows);
  }
  async getEvaluatorBoardData(formTemplateId?: string | null, includeUnassigned = false): Promise<EvalByEvaluatorRow[]> {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const appIds = this.appIdsForTemplate(formTemplateId);

    const evaluators = db.users.filter((u) => u.role === 'evaluator');

    const rows: EvalByEvaluatorRow[] = evaluators
      .map((ev) => {
        const asgns = db.assignments.filter(
          (a) => a.evaluatorId === ev.id && (!formTemplateId || appIds.has(a.applicationId))
        );
        if (asgns.length === 0) {
          if (!includeUnassigned) return null;
          const lastLoginTs = (ev as any).lastLoginAt ? new Date((ev as any).lastLoginAt).getTime() : 0;
          const lastActivityAt = lastLoginTs > 0 ? new Date(lastLoginTs).toISOString() : null;
          const daysSinceActivity = lastActivityAt ? (now - lastLoginTs) / DAY_MS : Infinity;
          const p = (db as any).evaluatorProfiles?.find((x: any) => x.evaluatorId === ev.id);
          return {
            evaluatorId: ev.id, evaluatorName: ev.name, email: ev.email,
            isExternal: (ev as any).isExternal ?? false,
            needsPasswordSetup: !((ev as any).isConfirmed ?? true),
            lastActivityAt, daysSinceActivity: isFinite(daysSinceActivity) ? daysSinceActivity : -1,
            maxDaysPending: -1,
            totalInvitations: 0, pendingCount: 0, notInvitedCount: 0, acceptedNotStartedCount: 0,
            inProgressCount: 0, completedCount: 0, declinedCount: 0, conflictCount: 0,
            activeLoad: 0, acceptedCount: 0,
            disponible: ALERT_CONFIG.OVERLOAD_THRESHOLD,
            isOverloaded: false, isInactive: false,
            assignments: [],
            adminProfile: { score: p?.score ?? null, latestNote: (p?.notes?.length ? p.notes[p.notes.length - 1].text : null) },
          } as EvalByEvaluatorRow;
        }

        // PROTOTYPE: lastActivityAt = max(lastLoginAt, last submitted eval) — see DEV NOTE in
        // checkAndGenerateAlerts(). In production this is a single user.lastActivityAt field.
        const lastLoginTs = (ev as any).lastLoginAt
          ? new Date((ev as any).lastLoginAt).getTime()
          : 0;
        const evalAsgIds = new Set(asgns.map((a) => a.id));
        const lastEvalTs = (db as any).evaluations
          .filter((e: any) => evalAsgIds.has(e.assignmentId) && e.submittedAt)
          .reduce((max: number, e: any) => Math.max(max, new Date(e.submittedAt).getTime()), 0);
        const lastActivityTs = Math.max(lastLoginTs, lastEvalTs);
        const lastActivityAt = lastActivityTs > 0 ? new Date(lastActivityTs).toISOString() : null;
        const daysSinceActivity = lastActivityAt
          ? (now - lastActivityTs) / DAY_MS
          : Infinity;

        const counts = {
          pending: asgns.filter((a) => a.status === 'pending').length,
          notInvited: asgns.filter(isNotInvited).length,
          acceptedNotStarted: asgns.filter((a) => a.status === 'accepted_not_started').length,
          inProgress: asgns.filter((a) => a.status === 'in_progress').length,
          completed: asgns.filter((a) => a.status === 'completed').length,
          declined: asgns.filter((a) => a.status === 'declined').length,
          conflict: asgns.filter((a) => a.status === 'conflict_declared').length,
        };
        const activeLoad = counts.pending + counts.acceptedNotStarted + counts.inProgress;

        const assignments: EvalByEvaluatorAssignment[] = asgns.map((a) => {
          const appRec = findApp(a.applicationId);
          return {
            assignmentId: a.id,
            applicationId: a.applicationId,
            applicationNumber: appRec?.applicationNumber ?? '—',
            title: (appRec as any)?.sectionAnswers?.PROJECT_INFORMATION?.projectTitle ?? appRec?.applicationNumber ?? '—',
            status: a.status as EvalByEvaluatorAssignment['status'],
            daysSinceAssigned: (now - new Date(a.assignedAt).getTime()) / DAY_MS,
            notInvited: isNotInvited(a),
            declineReason: (a as any).declineReason ?? null,
            declineNote: (a as any).declineNote ?? null,
          };
        });

        const activeStatuses = new Set(['pending', 'accepted_not_started', 'in_progress']);
        const maxDaysPending = assignments
          .filter((a) => activeStatuses.has(a.status))
          .reduce((max, a) => Math.max(max, a.daysSinceAssigned), -1);

        return {
          evaluatorId: ev.id,
          evaluatorName: ev.name,
          email: ev.email,
          isExternal: (ev as any).isExternal ?? false,
          needsPasswordSetup: !((ev as any).isConfirmed ?? true),
          lastActivityAt,
          daysSinceActivity: isFinite(daysSinceActivity) ? daysSinceActivity : -1,
          maxDaysPending,
          totalInvitations: asgns.length,
          pendingCount: counts.pending,
          notInvitedCount: counts.notInvited,
          acceptedNotStartedCount: counts.acceptedNotStarted,
          inProgressCount: counts.inProgress,
          completedCount: counts.completed,
          declinedCount: counts.declined,
          conflictCount: counts.conflict,
          activeLoad,
          acceptedCount: counts.acceptedNotStarted + counts.inProgress + counts.completed,
          // DEV NOTE (2026-07-01): Cuota acumulativa — cada asignación no-declined/conflict consume
          // un cupo permanentemente. En producción OVERLOAD_THRESHOLD vendrá de
          // Contest.maxAssignmentsPerEvaluator. La fórmula es: disponible = max − (activeLoad + completed).
          disponible: ALERT_CONFIG.OVERLOAD_THRESHOLD - (activeLoad + counts.completed),
          isOverloaded: (activeLoad + counts.completed) >= ALERT_CONFIG.OVERLOAD_THRESHOLD,
          isInactive: daysSinceActivity >= ALERT_CONFIG.EVALUATOR_LOGIN_DAYS && activeLoad > 0,
          assignments,
          adminProfile: (() => {
            const p = (db as any).evaluatorProfiles?.find((x: any) => x.evaluatorId === ev.id);
            const notes: any[] = p?.notes ?? [];
            return { score: p?.score ?? null, latestNote: notes.length ? notes[notes.length - 1].text : null };
          })(),
        };
      })
      .filter((r): r is EvalByEvaluatorRow => r !== null)
      .sort((a, b) => b.daysSinceActivity - a.daysSinceActivity);

    return delay(rows);
  }

  // ── Evaluator profile (EVA-012) ───────────────────────────────────────────

  getEvaluatorProfile(evaluatorId: string): Promise<any> {
    const profiles: any[] = (db as any).evaluatorProfiles ?? [];
    const profile = profiles.find((p: any) => p.evaluatorId === evaluatorId)
      ?? { evaluatorId, score: null, notes: [] };
    return delay({ ...profile });
  }

  async updateEvaluatorScore(evaluatorId: string, score: number | null): Promise<void> {
    const profiles: any[] = (db as any).evaluatorProfiles ?? [];
    const idx = profiles.findIndex((p: any) => p.evaluatorId === evaluatorId);
    if (idx >= 0) profiles[idx].score = score;
    else profiles.push({ evaluatorId, score, notes: [] });
    persist();
    return delay(undefined as any);
  }

  async addEvaluatorNote(evaluatorId: string, text: string): Promise<void> {
    const profiles: any[] = (db as any).evaluatorProfiles ?? [];
    const note = {
      id: newId('note'),
      text,
      createdAt: new Date().toISOString(),
      createdByName: 'Admin DINV',
    };
    const idx = profiles.findIndex((p: any) => p.evaluatorId === evaluatorId);
    if (idx >= 0) profiles[idx].notes = [...profiles[idx].notes, note];
    else profiles.push({ evaluatorId, score: null, notes: [note] });
    persist();
    return delay(undefined as any);
  }

  // ── Invitaciones pendientes de envío ──────────────────────────────────────
  //
  // Distinto del recordatorio: el recordatorio insiste sobre una invitación que
  // ya salió; esto envía la primera. La unidad es el EVALUADOR, no la
  // asignación — quien está en tres postulaciones recibe un solo correo con las
  // tres, así que "cuántos correos" y "cuántas asignaciones" son dos números
  // distintos y la UI tiene que mostrar ambos.

  /** Asignaciones sin invitar de un evaluador dentro del concurso. */
  private pendingInvitationsFor(evaluatorId: string, formTemplateId?: string | null) {
    const appIds = this.appIdsForTemplate(formTemplateId);
    return db.assignments.filter(
      (a) =>
        a.evaluatorId === evaluatorId &&
        isNotInvited(a) &&
        (!formTemplateId || appIds.has(a.applicationId))
    );
  }

  /**
   * Envía (simuladamente) una invitación por evaluador y marca todas sus
   * asignaciones pendientes como notificadas. Una sola escritura a localStorage:
   * un envío real cubre cientos de asignaciones.
   */
  async sendInvitations(
    evaluatorIds: string[],
    formTemplateId?: string | null
  ): Promise<{
    sentAt: string;
    emails: number;
    assignments: number;
    newEvaluators: number;
  }> {
    const sentAt = new Date().toISOString();
    let emails = 0;
    let assignments = 0;
    let newEvaluators = 0;

    evaluatorIds.forEach((evaluatorId) => {
      const pending = this.pendingInvitationsFor(evaluatorId, formTemplateId);
      if (pending.length === 0) return;
      pending.forEach((a) => {
        a.invitationSentAt = sentAt;
      });
      emails += 1;
      assignments += pending.length;
      if (!(findUser(evaluatorId)?.isConfirmed ?? true)) newEvaluators += 1;
    });

    persist();
    return delay({ sentAt, emails, assignments, newEvaluators }, 600);
  }

  // ── Reminders (EVA-018) ───────────────────────────────────────────────────

  getLastReminderForAssignments(assignmentIds: string[]): any {
    const reminders: any[] = (db as any).reminders ?? [];
    const relevant = reminders.filter((r: any) =>
      r.assignmentIds.some((id: string) => assignmentIds.includes(id))
    );
    if (!relevant.length) return null;
    return relevant.sort(
      (a: any, b: any) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
    )[0];
  }

  async sendReminder(params: {
    evaluatorId: string;
    assignmentIds: string[];
    templateType: 'invitation' | 'evaluation';
    message: string;
  }): Promise<void> {
    const reminders: any[] = (db as any).reminders ?? [];
    reminders.push({
      id: newId('rem'),
      ...params,
      sentAt: new Date().toISOString(),
      sentByName: 'Admin DINV',
    });
    persist();
    return delay(undefined as any);
  }

  async getBudgetTotals(formTemplateId?: string | null): Promise<{
    drafts: { montoSolicitado: number; montoContrapartes: number; count: number };
    submitted: { montoSolicitado: number; montoContrapartes: number; count: number };
  }> {
    await delay(60);
    const apps = this.appsForTemplate(formTemplateId);
    const drafts = apps.filter((a) => a.status === 'draft');
    const submitted = apps.filter((a) => a.status !== 'draft');

    const sumField = (list: typeof apps, field: 'montoSolicitado' | 'montoContrapartes') =>
      list.reduce((acc, a) => {
        const v = (a.sectionAnswers?.PROJECT_RESOURCES as any)?.budget?.[field];
        return acc + (typeof v === 'number' ? v : 0);
      }, 0);

    return {
      drafts: {
        montoSolicitado: sumField(drafts, 'montoSolicitado'),
        montoContrapartes: sumField(drafts, 'montoContrapartes'),
        count: drafts.length,
      },
      submitted: {
        montoSolicitado: sumField(submitted, 'montoSolicitado'),
        montoContrapartes: sumField(submitted, 'montoContrapartes'),
        count: submitted.length,
      },
    };
  }
}

const adminService = new AdminService();
export default adminService;
export { AdminServiceError };
